<script setup>
import { computed } from 'vue';

const emit = defineEmits(['toggle-favorite', 'open-duplicate', 'open-move', 'delete']);

const props = defineProps({
    page: {
        type: Object,
        required: true,
    },
    pageType: {
        type: String,
        required: true,
    },
    frontPageId: {
        type: Number,
        required: false,
        default: null,
    },
    customerAreaFrontPageId: {
        type: Number,
        required: false,
        default: null,
    },
    showFolders: {
        type: Boolean,
        required: false,
        default: true,
    },
    showLinks: {
        type: Boolean,
        required: false,
        default: true,
    },
    showEdit: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const isFrontPage = computed(() => props.frontPageId !== null && props.page.id === props.frontPageId);
const isCustomerAreaFrontPage = computed(() => props.customerAreaFrontPageId !== null && props.page.id === props.customerAreaFrontPageId);
const canDelete = computed(() => props.page.id !== props.frontPageId && props.page.id !== props.customerAreaFrontPageId);
const pageUrl = computed(() => (isFrontPage.value ? siteRoute('home') : siteRoute('landing-pages.show', props.page.slug)));
</script>

<template>
    <div class="flex flex-col overflow-hidden bg-white rounded-wl shadow-md">
        <div class="relative overflow-hidden aspect-square">
            <img
                v-if="page.img"
                :src="page.img"
                class="object-cover object-center w-full h-full"
                alt=""
            >
            <div
                v-else
                class="w-full h-full bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100"
            ></div>

            <div class="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>

            <div class="absolute top-3 left-3 flex items-center gap-2 text-white">
                <container-with-tooltip vertical-alignment="bottom">
                    <i
                        class="fa-solid  drop-shadow-md"
                        :class="page.public ? 'fa-unlock text-myflowGreen' : 'fa-lock text-myflowRed'"
                    ></i>
                    <template #tooltip>
                        <span class="text-gray-600">{{ page.public ? _mft('page:status.public') : _mft('page:status.notPublic') }}</span>
                    </template>
                </container-with-tooltip>

                <container-with-tooltip v-if="isFrontPage" vertical-alignment="bottom">
                    <i class="text-white fa-solid fa-store drop-shadow-md"></i>
                    <template #tooltip>
                        <span class="text-gray-600">{{ _mft('page:selectedAsPublicFrontPage') }}</span>
                    </template>
                </container-with-tooltip>

                <container-with-tooltip v-if="isCustomerAreaFrontPage" vertical-alignment="bottom">
                    <i class="text-white fa-solid fa-user drop-shadow-md"></i>
                    <template #tooltip>
                        <span class="text-gray-600">{{ _mft('page:selectedAsLoggedInFrontPage') }}</span>
                    </template>
                </container-with-tooltip>
            </div>

            <button
                type="button"
                class="absolute text-lg transition-colors top-3 right-3 text-yellow-300"
                @click.stop="emit('toggle-favorite', page)"
            >
                <i
                    class="fa-star drop-shadow-md"
                    :class="page.favorited ? 'fa-solid' : 'fa-regular text-white/80 hover:text-yellow-200'"
                ></i>
            </button>
        </div>

        <div class="flex flex-col flex-1 p-4">
            <div class="flex items-start justify-between gap-3">
                <h3 class="text-base font-semibold leading-snug break-words max-h-12 overflow-hidden">
                    {{ page.title }}
                    <span v-if="page.ai_page" class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">AI</span>
                </h3>
            </div>

            <div
                v-if="showFolders && page.folder_name"
                class="mt-2 text-xs text-gray-500"
            >
                <a :href="route('pages.showFolder', [pageType, page.folder_id])" class="mf-link">
                    <i class="fa-regular fa-folder"></i>
                    <span class="ml-1 italic">{{ page.folder_name }}</span>
                </a>
            </div>

            <div class="mt-auto pt-4">
                <div
                    v-if="showLinks"
                    class="mb-3"
                >
                    <link-field
                        class="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs"
                        :url="pageUrl"
                        :file-name="page.title+'.png'"
                    ></link-field>
                </div>

                <div class="flex flex-wrap items-center gap-2">
                    <a
                        v-if="showEdit"
                        class="flex-1"
                        :href="route('pages.edit', [pageType, page.id])"
                    >
                        <button-secondary size="sm" class="w-full justify-center">
                            {{ _mft('page:settings') }}
                        </button-secondary>
                    </a>

                    <a
                        class="flex-1"
                        :href="page.ai_page ? route('pages.ai-pagebuilder', [pageType, page.id]) : route('pages.pagebuilder', [pageType, page.id])"
                    >
                        <button-primary size="sm" class="w-full justify-center">
                            {{ _mft('shared:action.edit') }}
                        </button-primary>
                    </a>

                    <button-more-options
                        alignment="right"
                        position="top"
                        size="sm"
                        options-class="min-w-36"
                    >
                        <button-more-options-option-with-confirmation
                            v-if="canDelete"
                            style-type="danger"
                            :confirm-text="_mft('shared:confirm.areYouSure')"
                            :confirm-button-label="_mft('shared:action.delete')"
                            :abort-button-label="_mft('shared:action.cancel')"
                            @confirmed="emit('delete', page)"
                        >
                            <span class="text-xs">{{ _mft('shared:action.delete') }}</span>
                        </button-more-options-option-with-confirmation>
                        <button-more-options-option
                            class="whitespace-nowrap"
                            @click="emit('open-duplicate', page)"
                        >
                            <span class="text-xs">{{ _mft('shared:action.duplicate') }}</span>
                        </button-more-options-option>
                        <button-more-options-option
                            v-if="showFolders"
                            class="whitespace-nowrap"
                            @click="emit('open-move', page)"
                        >
                            <span class="text-xs">{{ _mft('page:folder.moveToFolder') }}</span>
                        </button-more-options-option>
                    </button-more-options>
                </div>
            </div>
        </div>
    </div>
</template>
